import React, { useEffect, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedSupplier } from '../../../store/brand/clientSlice';

export type TBrandModule = 'global' | 'campaign';

interface IBrandModuleSwitcherProps {
    activeModule: TBrandModule;
    onSwitchModule: (module: TBrandModule) => void;
}

const BRAND_MODULE_OPTIONS: { key: TBrandModule; logo: string; translationKey: string }[] = [
    {
        key: 'campaign',
        logo: '/icons/logos/img-campaign-logo.svg',
        translationKey: 'brand.header.switch_modal.campaign_module',
    },
    {
        key: 'global',
        logo: '/icons/logos/img-global-logo.svg',
        translationKey: 'brand.header.switch_modal.global_module',
    },
];

export const BrandModuleSwitcher = ({ activeModule, onSwitchModule }: IBrandModuleSwitcherProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [hoverSwitchIcon, setHoverSwitchIcon] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleSelectModule = (module: TBrandModule) => {
        if (module !== activeModule) {
            onSwitchModule(module); // TODO (ti): dispatch na redux ide unutar ovog callbacka, u Header-u
        }
        if (module === 'global') {
            navigate('/brand/global/dashboard');
        }
        setIsOpen(false);
    };

    return (
        <div ref={containerRef} className="position-relative mr-2">
            <div
                onMouseEnter={() => setHoverSwitchIcon(true)}
                onMouseLeave={() => setHoverSwitchIcon(false)}
                onClick={() => setIsOpen(prev => !prev)}
                className="icon-container cursor-pointer bg-white p-1"
                role="button"
                tabIndex={0}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-label={t('brand.header.switch_modal.toggle_label')}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setIsOpen(prev => !prev);
                    }
                }}
            >
                <i
                    className={`fi fi-rr-replace ${
                        hoverSwitchIcon || isOpen ? 'text-primary' : 'secondary-color'
                    } font-18
                        d-flex align-items-center justify-content-center`}
                />
            </div>

            <div
                role="dialog"
                aria-hidden={!isOpen}
                aria-label={t('brand.header.switch_modal.title')}
                className={`brand-module-switch-popover ${isOpen ? 'show' : ''}`}
            >
                {BRAND_MODULE_OPTIONS.map(option => (
                    <div
                        key={option.key}
                        className={`brand-module-option ${option.key} ${activeModule === option.key ? 'active' : ''}`}
                        onClick={() => {
                            handleSelectModule(option.key);
                            dispatch(setSelectedSupplier(undefined));
                        }}
                        role="button"
                        tabIndex={isOpen ? 0 : -1}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleSelectModule(option.key);
                                dispatch(setSelectedSupplier(undefined));
                            }
                        }}
                    >
                        <Image alt={t(option.translationKey)} src={option.logo} width={120} />
                    </div>
                ))}
            </div>
        </div>
    );
};
